import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  Loader2,
  CreditCard,
  ExternalLink,
  AlertTriangle,
  ShieldCheck,
  RefreshCw,
  Sparkles,
  Send,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GradientMesh from "@/components/GradientMesh";
import PlanUsageWidget from "@/components/PlanUsageWidget";
import ReportProblemDialog from "@/components/ReportProblemDialog";
import ComingSoonSection from "@/components/ComingSoonSection";
import ComingSoonBadge from "@/components/ComingSoonBadge";
import { COMING_SOON_BY_PLAN } from "@/lib/comingSoon";
import { LifeBuoy } from "lucide-react";

function fmtPrice(p) {
  if (p == null) return "0";
  const num = Number(p);
  if (Number.isNaN(num)) return String(p);
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

const PLAN_RANK = { free: 0, starter: 1, pro: 2, business: 3 };

export default function BillingPage() {
  const [params, setParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [sub, setSub] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [pollState, setPollState] = useState(null); // {status, sessionId}
  const [reportOpen, setReportOpen] = useState(false);
  const [reportContext, setReportContext] = useState({});
  const pollTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/subscription"),
      ]);
      setTiers(p.data?.tiers || []);
      setSub(s.data);
    } catch (_e) {
      toast.error("Could not load billing data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---- Success / cancel handling from the Stripe redirect ----
  useEffect(() => {
    const stripeFlag = params.get("stripe");
    const sessionId = params.get("session_id");
    if (stripeFlag === "cancel") {
      toast.info("Checkout cancelled — your plan is unchanged.");
      setParams({}, { replace: true });
      return;
    }
    if (stripeFlag === "success" && sessionId) {
      setPollState({ status: "pending", sessionId });
      pollSession(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollSession = useCallback(async (sessionId, attempt = 0) => {
    try {
      const res = await api.get(`/billing/status?session_id=${encodeURIComponent(sessionId)}`);
      const status = res.data?.payment_status;
      if (status === "paid" || status === "complete") {
        setPollState({ status: "paid", sessionId, plan: res.data?.plan });
        setSub((prev) => ({ ...(prev || {}), ...(res.data?.subscription || {}) }));
        toast.success("Subscription activated!");
        await load();
        setParams({}, { replace: true });
        return;
      }
      if (status === "expired" || status === "failed") {
        setPollState({ status, sessionId });
        toast.error("Payment didn't go through. Try again or use a different card.");
        setParams({}, { replace: true });
        return;
      }
      if (attempt > 12) {  // ~36s of polling
        setPollState({ status: "timeout", sessionId });
        toast.message("Still processing — refresh in a moment to see your new plan.");
        setParams({}, { replace: true });
        return;
      }
      pollTimer.current = setTimeout(() => pollSession(sessionId, attempt + 1), 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't verify payment status");
      setParams({}, { replace: true });
    }
  }, [load, setParams]);

  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  // ---- Subscribe / change plan ----
  const subscribe = async (tierId) => {
    if (tierId === "free") {
      // Free downgrade is only possible if user has no active Stripe sub.
      if (sub?.stripe_subscription_id) {
        toast.error("You have an active subscription — use 'Manage billing' to cancel.");
        return;
      }
      setBusyId(tierId);
      try {
        const res = await api.post("/billing/subscribe", { plan: "free" });
        setSub(res.data);
        toast.success("You're on the Free plan.");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not switch to Free");
      } finally {
        setBusyId(null);
      }
      return;
    }
    setBusyId(tierId);
    try {
      const res = await api.post("/billing/checkout", {
        plan: tierId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      // Handle the duplicate-subscription guard's 409 response as an
      // ACTIONABLE toast instead of the generic error surface. When the
      // backend detects an existing non-terminal Stripe subscription it
      // returns { detail: { code: "existing_subscription", message,
      // portal_url } } \u2014 we surface a "Manage subscription" action so
      // the user goes through the Stripe Customer Portal (which handles
      // proration + plan swap cleanly) rather than getting a raw error.
      const detail = e?.response?.data?.detail;
      const isExisting =
        e?.response?.status === 409 && detail && detail.code === "existing_subscription";
      if (isExisting) {
        const portalUrl = detail.portal_url;
        toast.info(
          detail.message || "You already have an active subscription. Manage your existing plan instead.",
          portalUrl
            ? {
                action: {
                  label: "Manage subscription",
                  onClick: () => {
                    window.location.href = portalUrl;
                  },
                },
                duration: 12000,
              }
            : { duration: 10000 },
        );
      } else {
        const msg =
          typeof detail === "string"
            ? detail
            : detail?.message || "Could not start checkout";
        toast.error(msg);
      }
      setBusyId(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const res = await api.post(`/billing/portal?origin_url=${encodeURIComponent(window.location.origin)}`);
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  const effectivePlan = sub?.effective_plan || sub?.plan || "free";
  const currentRank = PLAN_RANK[effectivePlan] ?? 0;
  const hasStripeSub = !!sub?.stripe_subscription_id;
  const cancelAtPeriodEnd = !!sub?.cancel_at_period_end;

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Billing & Subscription"
          subtitle="Real Stripe billing — secure checkout, monthly auto-renew, cancel anytime."
          testidPrefix="billing"
          action={
            hasStripeSub ? (
              <Button
                variant="outline"
                onClick={openPortal}
                disabled={portalBusy}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="billing-manage-portal"
              >
                {portalBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1.5" />}
                Manage billing
              </Button>
            ) : null
          }
        />

        {/* Post-checkout pending banner */}
        {pollState?.status === "pending" ? (
          <div
            className="mb-4 rounded-md border border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.05)] p-3 text-xs text-[color:var(--text-2)] flex items-center gap-2"
            data-testid="billing-pending-banner"
          >
            <Loader2 className="h-4 w-4 animate-spin text-[color:var(--brand-primary)]" />
            Confirming your payment with Stripe…
          </div>
        ) : null}

        {/* Past-due / failed payment banner */}
        {sub?.last_payment_status === "failed" || sub?.status === "past_due" ? (
          <div
            className="mb-4 rounded-md border border-[color:rgba(255,92,108,0.35)] bg-[color:rgba(255,92,108,0.05)] p-3 text-xs text-[color:var(--danger)] flex items-center gap-2 flex-wrap"
            data-testid="billing-failed-banner"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="text-[color:var(--text-2)]">
              Your last payment didn't go through. Open Manage billing to update your card.
            </span>
            <button
              type="button"
              onClick={() => {
                setReportContext({
                  category: "billing",
                  subject: "Payment failed / past due",
                  message: `Status: ${sub?.status || "—"}\nLast payment status: ${sub?.last_payment_status || "—"}\n\nDescribe what happened:`,
                });
                setReportOpen(true);
              }}
              className="ml-auto inline-flex items-center gap-1 text-[11px] text-[color:var(--text-2)] hover:text-[color:var(--text-1)] underline-offset-2 hover:underline"
              data-testid="billing-failed-report"
            >
              <LifeBuoy className="h-3.5 w-3.5" /> Report this issue
            </button>
          </div>
        ) : null}

        {/* Cancel-at-period-end notice */}
        {cancelAtPeriodEnd && sub?.current_period_end ? (
          <div
            className="mb-4 rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.05)] p-3 text-xs text-[color:var(--accent-warm)] flex items-center gap-2"
            data-testid="billing-cancel-banner"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="text-[color:var(--text-2)]">
              Your subscription is set to cancel on <strong>{fmtDate(sub.current_period_end)}</strong>. You'll drop to Free after that.
            </span>
          </div>
        ) : null}

        {/* Current plan banner - unified widget so usage matches Dashboard/Autopilot */}
        <div className="mb-5" data-testid="current-plan-banner">
          <PlanUsageWidget />
        </div>

        {/* Dual-cap explainer — every tier has TWO independent monthly caps */}
        <div
          className="mb-5 rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-4"
          data-testid="dual-cap-explainer"
        >
          <div className="flex items-start gap-3">
            <Info className="h-4 w-4 mt-0.5 text-[color:var(--brand-primary)] shrink-0" />
            <div className="text-sm text-[color:var(--text-2)] space-y-2">
              <div className="font-medium text-[color:var(--text-1)]">
                Every plan has two monthly caps — they're independent.
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-[13px]">
                <div className="flex items-start gap-2">
                  <Sparkles className="h-3.5 w-3.5 mt-0.5 text-[color:var(--brand-secondary)] shrink-0" />
                  <span>
                    <strong className="text-[color:var(--text-1)]">AI generations</strong> —
                    every time you ask Postmoro to draft a caption, image, or marketing
                    idea. Drafts &amp; regenerations both count. <em>Failed AI calls don't count.</em>
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Send className="h-3.5 w-3.5 mt-0.5 text-[color:var(--brand-primary)] shrink-0" />
                  <span>
                    <strong className="text-[color:var(--text-1)]">Published posts</strong> —
                    only counts when a post is <em>successfully published</em> to a connected
                    social account. Drafts, scheduled, and deleted posts are free.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((t, i) => {
            const isCurrent = effectivePlan === t.id;
            const tierRank = PLAN_RANK[t.id] ?? 0;
            const isUpgrade = tierRank > currentRank;
            const isDowngrade = tierRank < currentRank;
            const ctaLabel = isCurrent
              ? "Current plan"
              : t.id === "free"
              ? (hasStripeSub ? "Cancel in portal" : "Switch to Free")
              : (isUpgrade && hasStripeSub
                ? "Upgrade in portal"
                : isDowngrade && hasStripeSub
                ? "Downgrade in portal"
                : "Choose " + t.name);
            const usePortal = !isCurrent && hasStripeSub && t.id !== "free";
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, type: "spring", stiffness: 320, damping: 28 }}
                whileHover={{ y: -3 }}
                className={
                  "relative rounded-[16px] p-5 glass flex flex-col " +
                  (t.highlight ? "glow-primary" : "") +
                  (isCurrent ? " ring-1 ring-[color:var(--brand-primary)]" : "")
                }
                data-testid={`pricing-tier-card-${t.id}`}
              >
                {t.highlight ? (
                  <div className="absolute -top-2 right-4 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] font-semibold">
                    Most popular
                  </div>
                ) : null}
                {isCurrent ? (
                  <div className="absolute -top-2 left-4 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--success)] text-[color:var(--bg-0)] font-semibold">
                    Current
                  </div>
                ) : null}
                <div className="font-display text-lg font-semibold">{t.name}</div>
                <div className="text-xs text-[color:var(--text-3)] mt-1">{t.description}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-3xl font-semibold">${fmtPrice(t.price)}</span>
                  <span className="text-xs text-[color:var(--text-3)]">/ {t.period}</span>
                </div>
                {t.limits ? (
                  <div
                    className="mt-4 grid grid-cols-2 gap-2"
                    data-testid={`pricing-tier-limits-${t.id}`}
                  >
                    <div className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)]">
                        <Sparkles className="h-3 w-3 text-[color:var(--brand-secondary)]" />
                        AI gens / mo
                      </div>
                      <div
                        className="font-display text-lg font-semibold leading-tight mt-0.5"
                        data-testid={`pricing-tier-ai-limit-${t.id}`}
                      >
                        {t.limits.ai_generations == null
                          ? "Unlimited"
                          : t.limits.ai_generations.toLocaleString()}
                      </div>
                    </div>
                    <div className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)]">
                        <Send className="h-3 w-3 text-[color:var(--brand-primary)]" />
                        Published / mo
                      </div>
                      <div
                        className="font-display text-lg font-semibold leading-tight mt-0.5"
                        data-testid={`pricing-tier-publish-limit-${t.id}`}
                      >
                        {t.limits.published_posts == null
                          ? "Unlimited"
                          : t.limits.published_posts.toLocaleString()}
                      </div>
                      {t.limits.published_posts == null ? (
                        <div className="text-[10px] text-[color:var(--text-3)] mt-0.5">
                          fair-use
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <ul className="mt-4 space-y-2 text-sm text-[color:var(--text-2)] flex-1">
                  {(t.limits
                    ? t.features.filter(
                        (f) =>
                          !/\bAI generations?\s*\/\s*month\b/i.test(f) &&
                          !/\bpublished posts?\b/i.test(f),
                      )
                    : t.features
                  ).map((f) => (
                    <li key={f} className="flex gap-2">
                      <Check className="h-4 w-4 text-[color:var(--success)] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {(COMING_SOON_BY_PLAN[t.id] || []).length > 0 && (
                    <>
                      {(COMING_SOON_BY_PLAN[t.id] || []).map((f, i) => (
                        <li key={f} className={"flex gap-2 items-start opacity-90" + (i === 0 ? " pt-3 mt-2 border-t border-[color:var(--border-subtle)]" : "")} data-testid={`billing-coming-soon-row-${t.id}`}>
                          <Sparkles className="h-4 w-4 text-[color:rgb(255,190,110)] mt-0.5 shrink-0" aria-hidden="true" />
                          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                            <span>{f.replace(/\s*—\s*Coming Soon\s*$/i, "")}</span>
                            <ComingSoonBadge />
                          </span>
                        </li>
                      ))}
                    </>
                  )}
                </ul>
                <Button
                  onClick={() => (usePortal ? openPortal() : subscribe(t.id))}
                  disabled={busyId === t.id || isCurrent || portalBusy}
                  className={
                    "mt-5 w-full " +
                    (t.highlight
                      ? "bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                      : "bg-[color:var(--surface-3)] hover:bg-[color:var(--surface-2)] text-[color:var(--text-1)] border border-[color:var(--border-subtle)]")
                  }
                  data-testid={`pricing-select-plan-button-${t.id}`}
                >
                  {busyId === t.id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : usePortal ? (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  {ctaLabel}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-6 text-xs text-[color:var(--text-3)] flex items-center gap-2 flex-wrap">
          <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
          Payments are securely processed by Stripe. Cancel anytime — you keep access until the end of the billing period.
        </div>

        <div
          className="mt-3 text-xs text-[color:var(--text-3)] flex items-center gap-2 flex-wrap"
          data-testid="billing-help-footer"
        >
          <LifeBuoy className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
          Billing question or a charge that doesn't look right?{" "}
          <button
            type="button"
            onClick={() => {
              setReportContext({
                category: "billing",
                subject: "Billing question",
                message: "Describe your billing question or the charge in question:",
              });
              setReportOpen(true);
            }}
            className="text-[color:var(--brand-primary)] hover:underline"
            data-testid="billing-help-report"
          >
            Report a problem
          </button>{" "}
          or email{" "}
          <a
            href="mailto:support@postmoro.com"
            className="text-[color:var(--brand-primary)] hover:underline"
            data-testid="billing-help-email"
          >
            support@postmoro.com
          </a>
          .
        </div>
      </div>

      {/* Roadmap — display-only preview of upcoming Postmoro features.
          No card here triggers backend calls or billing changes. */}
      <ComingSoonSection
        eyebrow="Roadmap"
        title="Coming Soon to Postmoro"
        subtitle="These features aren&apos;t live yet — they&apos;re what we&apos;re building next. You&apos;ll see them in your plan automatically when they launch."
        className="mt-2"
      />

      <ReportProblemDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        context={reportContext}
      />
    </div>
  );
}
